import React from 'react';
import { Box, Typography, Paper, IconButton, Grid, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';

const ContactCard = styled(Paper)(({ theme }) => ({
  padding: '2.5rem',
  background: '#FFFFFF',
  borderRadius: '20px',
  height: '100%',
  border: '1px solid rgba(0, 0, 0, 0.12)',
  position: 'relative',
  boxShadow: 'none'
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  color: '#1A365D',
  fontSize: '1.75rem',
  fontWeight: 600,
  marginBottom: '2.5rem',
  position: 'relative',
  paddingBottom: '0.75rem',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '60px',
    height: '3px',
    background: '#2563EB'
  }
}));

const InfoSection = styled(Box)(({ theme }) => ({
  marginBottom: '2rem',
  padding: '1.25rem',
  background: 'rgba(37, 99, 235, 0.02)',
  borderRadius: '12px',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    background: 'rgba(37, 99, 235, 0.05)',
    transform: 'translateY(-2px)'
  },
  '&:last-child': {
    marginBottom: 0
  }
}));

const InfoTitle = styled(Typography)(({ theme }) => ({
  color: '#1A365D',
  fontSize: '1.1rem',
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  marginBottom: '1rem',
  '& .icon': {
    color: '#2563EB',
    fontSize: '1.25rem'
  }
}));

const InfoText = styled(Typography)(({ theme }) => ({
  color: '#475569',
  marginBottom: '0.75rem',
  paddingLeft: '2rem',
  fontSize: '0.95rem',
  lineHeight: 1.7,
  position: 'relative',
  '&:last-child': {
    marginBottom: 0
  }
}));

const ContactLink = styled(Link)(({ theme }) => ({
  color: '#475569',
  marginBottom: '0.5rem',
  display: 'flex',
  alignItems: 'center',
  paddingLeft: '2rem',
  fontSize: '0.95rem',
  position: 'relative',
  textDecoration: 'none',
  transition: 'all 0.2s ease-in-out',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '4px',
    height: '4px',
    borderRadius: '50%',
    backgroundColor: '#2563EB'
  },
  '&:hover': {
    color: '#2563EB',
    textDecoration: 'none'
  },
  '&:last-child': {
    marginBottom: 0
  }
}));

const SocialLinks = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '1rem',
  marginTop: '2.5rem',
  paddingTop: '1.5rem',
  borderTop: '1px solid #E2E8F0',
  '& .MuiIconButton-root': {
    color: '#1A365D',
    backgroundColor: 'rgba(37, 99, 235, 0.05)',
    padding: '12px',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: '#2563EB',
      color: '#FFFFFF',
      transform: 'translateY(-2px)'
    }
  }
}));

const ContactInfo = ({ 
  title,
  siegeAddress,
  laboCasaAddress,
  laboTangerAddress,
  phones,
  email,
  linkedin,
  instagram
}) => {
  return (
    <ContactCard>
      <CardTitle variant="h3">
        {title}
      </CardTitle>

      <InfoSection>
        <InfoTitle>
          <LocationOnIcon className="icon" />
          Adresse du siège
        </InfoTitle>
        <InfoText>{siegeAddress}</InfoText>
      </InfoSection>

      <InfoSection>
        <InfoTitle>
          <LocationOnIcon className="icon" />
          Laboratoire Casablanca
        </InfoTitle>
        <InfoText>{laboCasaAddress}</InfoText>
      </InfoSection>

      <InfoSection>
        <InfoTitle>
          <LocationOnIcon className="icon" />
          Laboratoire Tanger
        </InfoTitle>
        <InfoText>{laboTangerAddress}</InfoText>
      </InfoSection>

      <InfoSection>
        <InfoTitle>
          <PhoneIcon className="icon" />
          Contacts
        </InfoTitle>
        {phones.map((phone, index) => (
          <ContactLink 
            key={index} 
            href={`tel:${phone.replace(/\s+/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {phone}
          </ContactLink>
        ))}
      </InfoSection>

      <InfoSection>
        <InfoTitle>
          <EmailIcon className="icon" />
          Email
        </InfoTitle>
        <ContactLink
          href={`mailto:${email}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {email}
        </ContactLink>
      </InfoSection>

      <SocialLinks>
        <IconButton href={linkedin} target="_blank" aria-label="LinkedIn">
          <LinkedInIcon />
        </IconButton>
        <IconButton href={instagram} target="_blank" aria-label="Instagram">
          <InstagramIcon />
        </IconButton>
      </SocialLinks>
    </ContactCard>
  );
};

export default ContactInfo;
